import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import Autocomplete from '@mui/material/Autocomplete';
import { Formik, FormikErrors, FormikProps } from 'formik';
import {
  useGetKTreeInferenceOptions,
  useGetLimitedDropdownAssociteSubject,
  useTableDefinitioDetails,
  useTableDefinitionList,
  useUpsertTableDefinition,
} from 'services/v1/SystemTenant/AROKMS/TableDefinitionService';
import {
  dateTypeParameterOptions,
  dateTimeTypeParameterOptions,
  staticColumnParameterDropdownOption,
  staticTableParameterDropdownOption,
  depreciationResultOptions,
  ruleConditionOptions,
  concatenateConditionOptions,
} from 'data/Dropdown';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { useAttributeDropdown } from 'services/v1/SystemTenant/AROKMS/AttributeService';
import { useSubjectDropdown, useSubjectUsedDropdown } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import {
  TableDefinitionGetParams,
  TableDefinitionUpsertRequest,
} from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RemoveCircleOutlinedIcon from '@mui/icons-material/RemoveCircleOutlined';
import { useGetKTreeDefinitionListDropdown } from 'services/v1/SystemTenant/AROKMS/KTreeBuilderService';
import { RULES_CONSTANT } from 'constant/RuleConstant';
import { SUBJECT_TYPE } from 'constant/DataInputConstant';
import { getErrorMessage } from 'utils/Error';

import { generateYupSchema } from 'utils/SchemaGenerator';
import { isValidJSONString } from 'utils/Object';
import { ScoreMapperTable } from 'pages/SystemTenant/AROFunction/FunctionDefinition/components/ScoreMapperTable';
import { resolveSavedDropdownOption } from './resolveSavedDropdownOption';
import {
  ConcatenationFormulaEditor,
  ConcatenationSavedParameter,
} from './ConcatenationFormulaEditor';
import { buildKTreeDefinitionPayload } from '../utils/kTreeDefinitionPayload';
import { combineConcatenationAttributeOptions } from '../utils/concatenationFormula';
import {
  getUnresolvedDropdownFields,
  serializeRuleParameterValues,
} from '../utils/ruleParameterSerialization';

const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 700,
  maxWidth: 1020,
  bgcolor: 'background.paper',
  minHeight: 540,
  maxHeight: '80vh',
  overflow: 'auto',
  borderRadius: 1,
  px: 3,
  py: 2,
};

const autocompleteStyles = {
  width: '55%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};

const textInputStyles = {
  width: '55%',
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const textInputAutoCompleteStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

const optionStyles = { '& > span': { fontSize: '14px', mr: 2, flexShrink: 0 } };

export type ModalParamState = {
  tableDefinitionId: string | number | null;
  message: string;
  open: boolean;
};

interface ModalParamsProps {
  visible?: boolean;
  tableDefinitionId: string | number | null;
  onClose?: () => void;
  selectedTab: string;
  listFilter: TableDefinitionGetParams;
}

interface ParameterItem {
  label: string;
  id: string;
  type: string;
  optionsSource: string | null;
  required: boolean;
  removeTrim: boolean;
  defaultValue?: string | undefined | null | object;
  inline: boolean;
  placeholder?: string;
  validations?: [];
}

export interface AttributeParamDropdownFilter {
  tableDefinitionSubjectId: string | number | undefined;
  dataTypeId: string | number | undefined;
  ruleTypeId?: string | number | undefined;
  dependenciesAttributeId?: string | number | undefined;
}

const DEFAULT_EMPTY_OPTIONS = [{ label: '', value: '', dataType: null, persistentName: '' }];
const chipStyle = {
  fontSize: '10px',
};

type FormValues = {};
export function ModalParameter(props: ModalParamsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const [dependantDropdown, setDependantDropdown] = useState<string>('');
  const [kTreeInferenceCurrentLevel, setKTreeInferenceCurrentLevel] = useState<number>();
  const [concatenationFormulaError, setConcatenationFormulaError] = useState<string | null>(null);
  const { onClose, visible = false, tableDefinitionId, selectedTab, listFilter } = props;
  const [formFields, setFormFields] = useState<ParameterItem[]>([]);

  const paramsForTableDefinitionAssociateSubject = searchParams.get('recordAssociateSubjectId');
  const paramsForTableDefinitionAssociateAttribute = searchParams.get('recordAssociateAttributeId');
  const kTreeDefinitionId = searchParams.get('kTreeDefinitionId') || undefined;
  const inferenceKTreeDefinitionId = searchParams.get('inferenceKTreeDefinitionId') || undefined;
  const associatedSubjectId = searchParams.get('associatedSubjectId') || undefined;

  const [attributeDropdownFilter, setAttributeDropdownFilter] = useState<AttributeParamDropdownFilter>({
    tableDefinitionSubjectId: undefined,
    dependenciesAttributeId: undefined,
    dataTypeId: undefined,
    ruleTypeId: undefined,
  });
  const [attributeDropdownFilterDependant, setAttributeDropdownFilterDependant] =
    useState<AttributeParamDropdownFilter>({
      tableDefinitionSubjectId: undefined,
      dataTypeId: undefined,
    });

  const { data: subjectDropdownUsed, isLoading: isFetchingSubjectUsed } = useSubjectUsedDropdown({
    enabled: visible,
    subjectType: SUBJECT_TYPE.TABLE,
  });
  const { data: limitedDropdownSubject } = useGetLimitedDropdownAssociteSubject(listFilter.subjectId);
  const { data: subjectDropdown, isLoading: isFetchingSubject } = useSubjectDropdown(undefined, SUBJECT_TYPE.TABLE);
  const { data: attributeDropdown, isLoading: isFetchingAttribute } = useAttributeDropdown(attributeDropdownFilter);
  const { data: attributeDropdownDependant, isLoading: isFetchingAttributeDependant } = useAttributeDropdown(
    attributeDropdownFilterDependant
  );
  const { data: ktreeDefinitionOptions, isLoading: isLoadingKtreeOptions } = useGetKTreeDefinitionListDropdown(
    attributeDropdownFilterDependant.tableDefinitionSubjectId
  );
  const {
    data: kTreeInferenceOptions,
    isFetching: isFetchingKTreeInferenceOptions,
    isError: isKTreeInferenceOptionsError,
    error: kTreeInferenceOptionsError,
  } = useGetKTreeInferenceOptions(
    {
      kTreeDefinitionId: inferenceKTreeDefinitionId,
      currentLevel: kTreeInferenceCurrentLevel,
      associatedSubjectId,
    },
    visible
  );
  const {
    data: tableDefinitionDetails,
    isLoading,
    isFetching: isFetchingTableDefinitions,
  } = useTableDefinitioDetails({
    tableDefinitionId: tableDefinitionId || undefined,
    enabled: visible,
  });
  const isKTreeInferenceRule =
    tableDefinitionDetails?.data?.ruleType?.code ===
    RULES_CONSTANT.ASSOCIATED_SUBJECT.RULE_KTREE_PARENT_ATTRIBUTE_INFERENCE;
  const isConcatenateAttributesRule =
    tableDefinitionDetails?.data?.ruleType?.code === RULES_CONSTANT.TEXT.RULE_CONCATENATE_ATTRIBUTES;
  const { data: associatedSubjectFieldList, isLoading: isFetchingAssociatedSubjectFields } = useTableDefinitionList(
    {
      page: 0,
      size: 1000,
      subjectId: visible && isConcatenateAttributesRule ? listFilter.subjectId : '',
    },
    'ASSOCIATED_SUBJECT'
  );
  const {
    mutate: upsertTableDefinition,
    isLoading: isSubmitting,
    isError: isErrorUpsert,
    error: erroMessageUpsert,
  } = useUpsertTableDefinition(listFilter, selectedTab);

  const isAnyDataLoading =
    isFetchingSubject ||
    isFetchingAttribute ||
    isFetchingSubjectUsed ||
    isLoading ||
    isFetchingAttributeDependant ||
    isFetchingTableDefinitions ||
    isFetchingAssociatedSubjectFields ||
    isLoadingKtreeOptions;

  // tddtid => table definition data type id
  // use for filtering attribute dropdown to show only attribute with same data type
  useEffect(() => {
    if (searchParams.get('tddtid')) {
      setAttributeDropdownFilter((prev) => ({
        ...prev,
        dataTypeId: searchParams.get('tddtid') || undefined,
        ruleTypeId: searchParams.get('ruleTypeId') || undefined,
        dependenciesAttributeId: searchParams.get('dependenciesAttribute') || undefined,
        tableDefinitionSubjectId: tableDefinitionDetails?.data?.subject?.id || undefined,
      }));
    }
  }, [searchParams, tableDefinitionDetails]);

  useEffect(() => {
    setAttributeDropdownFilterDependant(attributeDropdownFilter);
  }, [attributeDropdownFilter]);

  const getDisabledDropdown = (fieldName: string, value: { [key: string]: string }) => {
    if (fieldName === 'ATTRIBUTE') {
      return value['SUBJECT'] === '';
    }
    if (fieldName === 'PARENT_NODE_ATTRIBUTE' || fieldName === 'SOURCE_ATTRIBUTE') {
      return !kTreeInferenceOptions?.data;
    }
    return false;
  };

  const getDisabledDynamicDropdown = (
    formFields: ParameterItem[],
    fieldName: string,
    value: { [key: string]: string }
  ) => {
    // disable dropdown if the value of the previous field is empty
    const index = formFields.findIndex((item) => item.id === fieldName);

    if (index > 0) {
      const previousField = formFields[index - 1];
      return value[previousField.id] === '' || value[previousField.id] === undefined;
    }
    return false;
  };

  const getFieldTypes = (
    field: string,
    formikOptions: {
      loading: boolean;
      errors: FormikErrors<{ [key: string]: string | number }>;
      values: { [key: string]: any };
      removeTrim?: boolean;
      setAttributeDropdownFilterDependant?: (params: AttributeParamDropdownFilter) => void;
      options: { value: string | number; label: string; dataType?: string | null }[];
      placeholder?: string;
      fieldName: string;
      setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
    },
    index: number,
    formFields: ParameterItem[]
  ) => {
    switch (field) {
      case 'textinput':
        return (
          <TextField
            value={formikOptions.values[formikOptions.fieldName]}
            sx={textInputStyles}
            size='small'
            placeholder={formikOptions.placeholder || ''}
            error={formikOptions.errors[formikOptions.fieldName] ? true : false}
            helperText={formikOptions.errors[formikOptions.fieldName]}
            onChange={(e) => {
              if (!formikOptions.removeTrim) {
                formikOptions.setFieldValue(formikOptions.fieldName, e.target.value);
                return;
              }
              formikOptions.setFieldValue(formikOptions.fieldName, e.target.value.trim());
            }}
          />
        );
      case 'numberinput':
        return (
          <TextField
            value={formikOptions.values[formikOptions.fieldName]}
            sx={textInputStyles}
            size='small'
            placeholder={formikOptions.placeholder || ''}
            type='number'
            error={formikOptions.errors[formikOptions.fieldName] ? true : false}
            helperText={formikOptions.errors[formikOptions.fieldName]}
            onChange={(e) => {
              const nextValue = formikOptions.removeTrim ? e.target.value : e.target.value.trim();
              formikOptions.setFieldValue(formikOptions.fieldName, nextValue);

              if (formikOptions.fieldName === 'CURRENT_LEVEL') {
                const parsedLevel = nextValue === '' ? undefined : Number(nextValue);
                setKTreeInferenceCurrentLevel(
                  parsedLevel !== undefined && Number.isInteger(parsedLevel) ? parsedLevel : undefined
                );
                formikOptions.setFieldValue('PARENT_NODE_ATTRIBUTE', '');
                formikOptions.setFieldValue('SOURCE_ATTRIBUTE', '');
              }
            }}
          />
        );
      case 'switch-toggle':
        return (
          <>
            <Switch
              checked={formikOptions.values[formikOptions.fieldName] === 'true'}
              inputProps={{ 'aria-label': 'controlled' }}
              onChange={(e) => {
                formikOptions.setFieldValue(formikOptions.fieldName, `${e.target.checked}`);
              }}
            />
          </>
        );
      case 'dropdown':
        return (
          <Autocomplete
            key={formikOptions.fieldName}
            value={{
              label:
                typeof formikOptions.values[formikOptions.fieldName] === 'object'
                  ? formikOptions.values[formikOptions.fieldName]?.label || ''
                  : formikOptions.values[formikOptions.fieldName] || '',
              value:
                typeof formikOptions.values[formikOptions.fieldName] === 'object'
                  ? formikOptions.values[formikOptions.fieldName]?.value || ''
                  : formikOptions.values[formikOptions.fieldName] || '',
              dataType: '',
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            clearIcon={null}
            disabled={getDisabledDynamicDropdown(formFields, formikOptions.fieldName, formikOptions.values)}
            contentEditable={false}
            options={formikOptions.options}
            loading={formikOptions.loading}
            onChange={(event, value) => {
              if (formikOptions.fieldName === 'SUBJECT' && value?.value) {
                setAttributeDropdownFilterDependant({ tableDefinitionSubjectId: value.value, dataTypeId: undefined });
                formikOptions.setFieldValue('ATTRIBUTE', '');
                formikOptions.setFieldValue('WHEREATTRIBUTE', '');
                formikOptions.setFieldValue('TARGETATTRIBUTE', '');
              }
              if (formikOptions.fieldName === 'STABLE' && value?.value) {
                setDependantDropdown(value?.value as string);
                formikOptions.setFieldValue('SCOLUMN', '');
              }
              formikOptions.setFieldValue(formikOptions.fieldName, value);
            }}
            renderOption={(props, option) => (
              <Box component='li' sx={optionStyles} {...props}>
                <span>{option?.label}</span>
                {option?.dataType && (
                  <Chip label={option.dataType} color='info' variant='outlined' size='small' sx={chipStyle} />
                )}
              </Box>
            )}
            sx={autocompleteStyles}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={textInputAutoCompleteStyles}
                error={formikOptions.errors[formikOptions.fieldName] ? true : false}
                helperText={formikOptions.errors[formikOptions.fieldName]}
              />
            )}
          />
        );
      case 'dropdown-dynamic':
        return (
          <Stack direction='row' display='flex' flex={1} alignItems='center' justifyContent='flex-end'>
            <Autocomplete
              key={formikOptions.fieldName}
              value={{
                label:
                  typeof formikOptions.values[formikOptions.fieldName] === 'object'
                    ? formikOptions.values[formikOptions.fieldName]?.label || ''
                    : formikOptions.values[formikOptions.fieldName] || '',
                value:
                  typeof formikOptions.values[formikOptions.fieldName] === 'object'
                    ? formikOptions.values[formikOptions.fieldName]?.value || ''
                    : formikOptions.values[formikOptions.fieldName] || '',
                dataType: '',
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              clearIcon={null}
              disabled={getDisabledDropdown(formikOptions.fieldName, formikOptions.values)}
              contentEditable={false}
              options={formikOptions.options}
              loading={formikOptions.loading}
              onChange={(event, value) => {
                if (formikOptions.fieldName === 'SUBJECT' && value?.value) {
                  setAttributeDropdownFilterDependant({ tableDefinitionSubjectId: value.value, dataTypeId: undefined });
                  formikOptions.setFieldValue('ATTRIBUTE', '');
                }
                if (formikOptions.fieldName === 'STABLE' && value?.value) {
                  setDependantDropdown(value?.value as string);
                  formikOptions.setFieldValue('SCOLUMN', '');
                }
                formikOptions.setFieldValue(formikOptions.fieldName, value);
              }}
              renderOption={(props, option) => (
                <Box component='li' sx={optionStyles} {...props}>
                  <span>{option?.label}</span>
                  {option.dataType && (
                    <Chip label={option.dataType} color='info' variant='outlined' size='small' sx={chipStyle} />
                  )}
                </Box>
              )}
              sx={{ ...autocompleteStyles }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={textInputAutoCompleteStyles}
                  error={formikOptions.errors[formikOptions.fieldName] ? true : false}
                  helperText={formikOptions.errors[formikOptions.fieldName]}
                />
              )}
            />
            {!isKTreeInferenceRule && (
              <Tooltip title='Add column'>
                <IconButton
                  onClick={() => {
                    setFormFields((prev) => [
                      ...prev,
                      {
                        label: `Attribute ${formFields.length + 1}`,
                        id: `ATTRIBUTE${formFields.length + 1}`,
                        type: 'dropdown-dynamic',
                        optionsSource: prev[0].optionsSource,
                        required: false,
                        removeTrim: false,
                        placeholder: '',
                        inline: false,
                        defaultValue: { label: '', value: '' },
                      },
                    ]);
                  }}
                >
                  <AddCircleOutlinedIcon fontSize='medium' sx={{ fill: '#00B3DC' }} />
                </IconButton>
              </Tooltip>
            )}

            {!isKTreeInferenceRule && (
              <Tooltip title='Delete' disableHoverListener={index === 0} disableInteractive={index === 0}>
                <IconButton
                  disabled={index === 0}
                  onClick={() => {
                    setFormFields((prev) => prev.filter((_, i) => i !== index));
                  }}
                >
                  <RemoveCircleOutlinedIcon fontSize='medium' sx={{ fill: '#00B3DC', opacity: index === 0 ? 0 : 1 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );

      case 'score-mapping-table':
        return (
          <Stack direction='column' spacing={2} mt={2} width='100%'>
            <ScoreMapperTable
              initialData={formikOptions?.values?.[formikOptions.fieldName] ? JSON.parse(formikOptions?.values?.[formikOptions.fieldName]) : []} 
              onDataChange={(data) => {
                formikOptions.setFieldValue(formikOptions.fieldName, JSON.stringify(data));
              }}
              initialSubjectSource={formikOptions.values['SUBJECT']}
            />
          </Stack>
        );
    }
  };

  const getDropdownOptions = (
    field: string | null
  ): { label: string; value: string | number; dataType?: string | null; persistentName?: string | null }[] => {
    switch (field) {
      case 'ATTRIBUTE_DROPDOWN':
        return (
          attributeDropdown?.data
            .filter((item) => item?.label !== paramsForTableDefinitionAssociateAttribute)
            .map((item) => ({
              label: item?.label,
              value: item?.value,
              dataType: item?.dataType,
              persistentName: item?.persistentName,
            })) || DEFAULT_EMPTY_OPTIONS
        );
      case 'ATTRIBUTE_DROPDOWN_DEPENDANT':
        const options =
          attributeDropdownDependant?.data.map((item) => ({
            label: item?.label,
            value: item?.value,
            dataType: item?.dataType || '',
            persistentName: item?.persistentName,
          })) || DEFAULT_EMPTY_OPTIONS;
        return options;
      case 'KTREE_PARENT_NODE_ATTRIBUTE_DROPDOWN':
        return (
          kTreeInferenceOptions?.data.parentNodeAttributes.map((item) => ({
            label: item.label,
            value: item.value,
            dataType: item.type,
            persistentName: item.label,
          })) || DEFAULT_EMPTY_OPTIONS
        );
      case 'KTREE_PARENT_SOURCE_ATTRIBUTE_DROPDOWN':
        return (
          kTreeInferenceOptions?.data.sourceAttributes.map((item) => ({
            label: item.label,
            value: item.value,
            dataType: item.type,
            persistentName: item.label,
          })) || DEFAULT_EMPTY_OPTIONS
        );
      case 'SUBJECT_DROPDOWN':
        return subjectDropdownUsed?.data || DEFAULT_EMPTY_OPTIONS;
      case 'KTREE_DROPDOWN':
        return (
          ktreeDefinitionOptions?.data.map((item) => ({ ...item, persistentName: item.label })) || DEFAULT_EMPTY_OPTIONS
        );
      case 'SUBJECT_DROPDOWN_ALL': {
        return (
          subjectDropdown?.data.map((item) => ({
            id: item.id,
            value: item.id,
            label: item.subject,
            persistentName: item.subject,
          })) || DEFAULT_EMPTY_OPTIONS
        );
      }
      case 'LIMITED_SUBJECT_LIST_DROPDOWN':
        return (
          limitedDropdownSubject?.data.filter((item) => item?.label !== paramsForTableDefinitionAssociateSubject) ||
          DEFAULT_EMPTY_OPTIONS
        );

      case 'RULE_CONDITION_TYPES_OPTIONS':
        return ruleConditionOptions;

      case 'DATE_TYPE_DROPDOWN':
        return dateTypeParameterOptions?.map((item) => ({ ...item, persistentName: item.label }));

      case 'CONCATE_RULES_OPTIONS':
        return concatenateConditionOptions;
      case 'DATETIME_TYPE_DROPDOWN':
        return dateTimeTypeParameterOptions;
      case 'STATIC_TABLE_DROPDONW':
        return staticTableParameterDropdownOption;
      case 'F_DEPRECIATION_RESULT':
        return depreciationResultOptions;
      case 'STATIC_COLUMN_DROPDOWN':
        // @ts-ignore
        return staticColumnParameterDropdownOption[dependantDropdown];
      default:
        return DEFAULT_EMPTY_OPTIONS;
    }
  };

  const concatenationAttributeOptions = useMemo(
    () =>
      combineConcatenationAttributeOptions(
        attributeDropdown?.data
          .filter((item) => item?.label !== paramsForTableDefinitionAssociateAttribute)
          .map((item) => ({
            label: item?.label,
            value: item?.value,
            dataType: item?.dataType,
            persistentName: item?.persistentName,
          })) || [],
        associatedSubjectFieldList?.data?.data || [],
        tableDefinitionId
      ),
    [
      associatedSubjectFieldList?.data?.data,
      attributeDropdown?.data,
      paramsForTableDefinitionAssociateAttribute,
      tableDefinitionId,
    ]
  );

  const getDropdownLoading = (field: string | null): boolean => {
    switch (field) {
      case 'ATTRIBUTE_DROPDOWN':
        return isFetchingAttribute;
      case 'KTREE_PARENT_NODE_ATTRIBUTE_DROPDOWN':
      case 'KTREE_PARENT_SOURCE_ATTRIBUTE_DROPDOWN':
        return isFetchingKTreeInferenceOptions;
      case 'SUBJECT_DROPDOWN':
        return isFetchingSubject;

      case 'SUBJECT_DROPDOWN_ALL': {
        return isFetchingSubjectUsed;
      }
      default:
        return false;
    }
  };

  const handleOnCancel = () => {
    handleOnCloseModal?.();
  };

  const handleOnCloseModal = () => {
    //  Clear query params
    searchParams.delete('tddtid');
    searchParams.delete('recordAssociateSubjectId');
    searchParams.delete('recordAssociateAttributeId');
    searchParams.delete('ruleTypeId');
    searchParams.delete('dependenciesAttribute');
    searchParams.delete('kTreeDefinitionId');
    searchParams.delete('inferenceKTreeDefinitionId');
    searchParams.delete('associatedSubjectId');
    setSearchParams(searchParams);
    setKTreeInferenceCurrentLevel(undefined);
    onClose?.();
  };

  const handleOnSave = (data: { [key: string]: string | number }) => {
    if (tableDefinitionDetails?.data) {
      const unresolvedDropdownFields = getUnresolvedDropdownFields(formFields, data);
      if (unresolvedDropdownFields.length > 0) return;

      const serializedParameters = serializeRuleParameterValues(formFields, data);
      const payloadRequest: TableDefinitionUpsertRequest = {
        ruleTypeId: tableDefinitionDetails.data.ruleType.id,
        subjectId: tableDefinitionDetails.data.subject.id,
        allowEmpty: tableDefinitionDetails.data.allowEmpty,
        id: tableDefinitionId,
        ...buildKTreeDefinitionPayload({
          ruleTypeCode: tableDefinitionDetails.data.ruleType.code,
          ktreeDefinitionId: kTreeDefinitionId || tableDefinitionDetails.data.kTreeDefinition?.id,
          inferenceKTreeDefinitionId:
            inferenceKTreeDefinitionId ||
            tableDefinitionDetails.data.inferenceKTreeDefinition?.value ||
            tableDefinitionDetails.data.inferenceKTreeDefinitionId,
        }),
        ruleTypeParams: serializedParameters.ruleTypeParams,
        ruleTypeParamsId: serializedParameters.ruleTypeParamsId,
      };

      if (isKTreeInferenceRule) {
        const currentNodeAttribute = data.CURRENT_NODE_ATTRIBUTE as unknown as {
          label?: string;
          persistentName?: string;
          value?: string;
        };
        const parentNodeAttribute = data.PARENT_NODE_ATTRIBUTE as unknown as {
          label?: string;
          persistentName?: string;
          value?: string;
        };
        const sourceAttribute = data.SOURCE_ATTRIBUTE as unknown as {
          label?: string;
          persistentName?: string;
          value?: string;
        };

        payloadRequest.ruleTypeParams = [
          data.CURRENT_LEVEL,
          currentNodeAttribute.persistentName || currentNodeAttribute.label,
          parentNodeAttribute.persistentName || parentNodeAttribute.label,
          sourceAttribute.persistentName || sourceAttribute.label,
        ].join(',');
        payloadRequest.ruleTypeParamsId = [
          `CURRENT_LEVEL=${data.CURRENT_LEVEL}`,
          `CURRENT_NODE_ATTRIBUTE=${currentNodeAttribute.value}`,
          `PARENT_NODE_ATTRIBUTE=${parentNodeAttribute.value}`,
          `SOURCE_ATTRIBUTE=${sourceAttribute.value}`,
        ].join(',');
      }

      // if ruleType id is "52" which means this is rule for "SELECT FROM KTREE TABLE"
      // we need send id, instead of name
      //@ts-ignore
      if (tableDefinitionDetails.data.ruleType.code === RULES_CONSTANT.TEXT.RULE_SELECT_FROM_KTREE_TABLE) {
        // @ts-ignore
        payloadRequest.ruleTypeParams = `${data.SUBJECT.persistentName},${data.KTREE.value}`;
      }

      if (isConcatenateAttributesRule && payloadRequest.ruleTypeParams) {
        // const ruleTypeParamsId =
        const columnNames = payloadRequest.ruleTypeParams
          .split(',')
          .filter((string) => string.startsWith('{') && string.endsWith('}'))
          .map((string) => string.slice(1, -1));

        const columnNamesSet = new Set(columnNames);

        const persistentColumnNamesWithValue = concatenationAttributeOptions.filter((dropdownItem) => {
          return columnNamesSet.has(dropdownItem.label);
        });

        const ruleTypeParamsId = persistentColumnNamesWithValue
          .map((item) => `${item?.persistentName}=${item?.value}`)
          .filter(Boolean)
          .join();
        payloadRequest.ruleTypeParamsId = ruleTypeParamsId;
        payloadRequest.ruleTypeParams = JSON.stringify(data);
      }

      if (selectedTab === 'ASSOCIATED_ATTRIBUTE') {
        payloadRequest.attributeId = tableDefinitionDetails.data.attribute.id;
      }
      if (selectedTab === 'ASSOCIATED_SUBJECT') {
        payloadRequest.displayName = tableDefinitionDetails.data.associatedSubjectDefinition.displayName;
        payloadRequest.associatedSubjectId =
          tableDefinitionDetails.data.associatedSubjectDefinition.associatedSubject.id;
      }

      upsertTableDefinition(payloadRequest, {
        onSuccess: () => {
          handleOnCloseModal?.();
        },
      });
    }
  };

  const getModalTitle = useCallback(() => {
    if (!tableDefinitionDetails?.data) return '';

    if (selectedTab === 'ASSOCIATED_ATTRIBUTE' && tableDefinitionDetails?.data) {
      return `Parameter for ${tableDefinitionDetails?.data.attribute.attribute}`;
    }
    return `Parameter for ${tableDefinitionDetails?.data.associatedSubjectDefinition.associatedSubject.subject}`;
  }, [selectedTab, tableDefinitionDetails]);

  const parameterFields: ParameterItem[] = useMemo(() => {
    if (tableDefinitionDetails?.data?.ruleType?.paramsValue) {
      return JSON.parse(tableDefinitionDetails?.data.ruleType.paramsValue);
    }
    return [];
  }, [tableDefinitionDetails]);

  const yupSchema = useMemo(() => {
    if (parameterFields.length > 0) {
      // @ts-ignore
      return parameterFields.reduce(generateYupSchema, {});
    }
    return false;
  }, [parameterFields]);

  const validationSchema: {
    isValidSync?: (data: { [key: string]: string | number }) => boolean;
  } = useMemo(() => {
    // @ts-ignore
    return yupSchema ? yup.object().shape(yupSchema) : {};
  }, [yupSchema]);

  const formikInitialValues = useMemo(() => {
    if (formFields.length > 0) {
      if (tableDefinitionDetails?.data.ruleTypeParams) {
        const params = tableDefinitionDetails?.data.ruleTypeParams.split(',');
        const paramsIds = tableDefinitionDetails?.data.ruleTypeParamsTableDefinitionId?.split(',') || [];

        return formFields.reduce((acc, item, index) => {
          if (item.id === 'ATTRIBUTE_CONCATENATION') {
            let obcAttrbConcate = tableDefinitionDetails?.data.ruleTypeParams;
            let ruleParamsOBJ;
            if (isValidJSONString(tableDefinitionDetails?.data.ruleTypeParams)) {
              ruleParamsOBJ = JSON.parse(tableDefinitionDetails?.data.ruleTypeParams);
              obcAttrbConcate = ruleParamsOBJ['ATTRIBUTE_CONCATENATION'];
            }

            return { ...acc, [item.id]: obcAttrbConcate };
          }
          if (item.id === 'ATTRIBUTE_CONCATENATION_RULES') {
            let obcAttrbConcate = tableDefinitionDetails?.data.ruleTypeParams;
            let ruleParamsOBJ;
            if (isValidJSONString(tableDefinitionDetails?.data.ruleTypeParams)) {
              ruleParamsOBJ = JSON.parse(tableDefinitionDetails?.data.ruleTypeParams);
              obcAttrbConcate = ruleParamsOBJ['ATTRIBUTE_CONCATENATION_RULES'];
              return { ...acc, [item.id]: obcAttrbConcate };
            }
          }
          if (item.type === 'dropdown-dynamic' || item.type === 'dropdown') {
            const options = getDropdownOptions(item.optionsSource);
            const option = resolveSavedDropdownOption(options, params[index], paramsIds[index]);

            if (option) {
              return { ...acc, [item.id]: option };
            }

          }

          if(item.type === 'score-mapping-table'){
            const params = tableDefinitionDetails?.data.ruleTypeParams
            // get last index
            const scoreMappingParams = params.match(/\[.*\]/);
           

            if (scoreMappingParams) {
              const json = scoreMappingParams[0];
              return { ...acc, [item.id]: json };
            }
          }


          // 
          return { ...acc, [item.id]: params[index] };
        }, {});
      }
      const parameters = formFields.map((item) => {
        return {
          [item.id]: '',
        };
      });

      return Object.assign({}, ...parameters);
    }
    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formFields,
    tableDefinitionDetails?.data.ruleTypeParams,
    attributeDropdown,
    kTreeInferenceOptions,
    subjectDropdown,
    subjectDropdownUsed,
  ]);

  const isButtonSubmitDisabled = (values: { [key: string]: string | number }) => {
    //  Check if with required fields form parameterFields is filled
    const isRequiredFieldsEmpty = parameterFields.some((item) => {
      if (item.required) {
        return values[item.id] === '' || values[item.id] === undefined || values[item.id] === null;
      }
      return false;
    });

    if (isKTreeInferenceRule) {
      const currentLevel = Number(values.CURRENT_LEVEL);
      const hasValidCurrentLevel = Number.isInteger(currentLevel) && currentLevel >= 0 && currentLevel <= 8;
      const hasDropdownValue = (fieldName: string) => {
        const value = values[fieldName] as unknown as { value?: string };
        return typeof value === 'object' && Boolean(value?.value);
      };

      return (
        isRequiredFieldsEmpty ||
        !hasValidCurrentLevel ||
        !hasDropdownValue('CURRENT_NODE_ATTRIBUTE') ||
        !hasDropdownValue('PARENT_NODE_ATTRIBUTE') ||
        !hasDropdownValue('SOURCE_ATTRIBUTE')
      );
    }

    if (isConcatenateAttributesRule && concatenationFormulaError) return true;

    return isRequiredFieldsEmpty || getUnresolvedDropdownFields(formFields, values).length > 0;
  };

  useEffect(() => {
    if (
      parameterFields.length > 0 &&
      tableDefinitionDetails?.data.ruleTypeParams &&
      parameterFields.some((item) => item.type === 'dropdown-dynamic') &&
      !isKTreeInferenceRule
    ) {
      const params = tableDefinitionDetails?.data.ruleTypeParams.split(',');

      // by default the fields will be 2 fields
      if (params.length) {
        // remove the first 2 fields
        const initialFieldsBasedOnParams = params.slice(2);
        const initialFields = initialFieldsBasedOnParams.map((item, index) => {
          return {
            label: `Attribute ${index + 3}`,
            id: `ATTRIBUTE${index + 3}`,
            type: 'dropdown-dynamic',
            optionsSource: parameterFields[0].optionsSource,
            required: false,
            removeTrim: false,
            placeholder: '',
            inline: false,
          };
        });
        setFormFields([...parameterFields.slice(0, 2), ...initialFields]);
      }
    } else {
      setFormFields(parameterFields);
    }
  }, [isKTreeInferenceRule, parameterFields, tableDefinitionDetails?.data.ruleTypeParams]);

  useEffect(() => {
    if (Object.keys(formikInitialValues).length > 0) {
      const initialCurrentLevel = formikInitialValues?.CURRENT_LEVEL;
      const parsedCurrentLevel = Number(initialCurrentLevel);
      if (initialCurrentLevel !== '' && Number.isInteger(parsedCurrentLevel)) {
        setKTreeInferenceCurrentLevel(parsedCurrentLevel);
      }

      if (formikInitialValues?.KTREE) {
        const KtreeLabel =
          tableDefinitionDetails?.data?.ruleTypeParamsTableDefinitionId?.split(',')?.[1].split('=')?.[0] ||
          'Unknown Ktree';

        const ktreeInitialValue = {
          label: KtreeLabel,
          value: formikInitialValues?.KTREE,
        };

        formikRef.current?.setValues({
          ...formikRef.current?.values,
          ...formikInitialValues,
          KTREE: ktreeInitialValue,
        });
      } else {
        formikRef.current?.setValues({
          ...formikRef.current?.values,
          ...formikInitialValues,
        });
      }
    }
    if (formikInitialValues?.SUBJECT && formikInitialValues?.SUBJECT?.value) {
      setAttributeDropdownFilterDependant({
        tableDefinitionSubjectId: formikInitialValues.SUBJECT.value || '',
        dataTypeId: attributeDropdownFilter.dataTypeId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikInitialValues]);

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal}>
        {isAnyDataLoading ? (
          <Stack direction='column' sx={ModalContentStyle} justifyContent='center'>
            <Stack justifyContent='space-between' direction='row' alignItems='center' spacing={2}>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                {getModalTitle()}
              </Typography>
              <IconButton onClick={handleOnCloseModal} component='label'>
                <HighlightOffRoundedIcon sx={closeIconStyle} />
              </IconButton>
            </Stack>
            <ActivityIndicator />
          </Stack>
        ) : (
          <Stack direction='column' sx={ModalContentStyle} justifyContent='space-between'>
            <Stack>
              <Stack justifyContent='space-between' direction='row' alignItems='center' spacing={2}>
                <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                  {getModalTitle()}
                </Typography>
                <IconButton onClick={handleOnCloseModal} component='label'>
                  <HighlightOffRoundedIcon sx={closeIconStyle} />
                </IconButton>
              </Stack>
              <Divider sx={{ mb: 1 }} />
            </Stack>
            <Formik
              innerRef={formikRef}
              initialValues={formikInitialValues}
              onSubmit={handleOnSave}
              validationSchema={validationSchema}
            >
              {({ handleSubmit, values, setFieldValue, errors, touched }) => {
                const displayedFormFields = isConcatenateAttributesRule
                  ? formFields.filter((field) => field.id !== 'ATTRIBUTE_CONCATENATION')
                  : formFields;
                const unresolvedDropdownFields = getUnresolvedDropdownFields(formFields, values);

                return (
                  <>
                    <Stack>
                      {isConcatenateAttributesRule && (
                        <ConcatenationFormulaEditor
                          value={String(values.ATTRIBUTE_CONCATENATION || '')}
                          attributeOptions={concatenationAttributeOptions}
                          onChange={(nextValue) => setFieldValue('ATTRIBUTE_CONCATENATION', nextValue, true)}
                          onValidationChange={setConcatenationFormulaError}
                        />
                      )}
                      {displayedFormFields.some((item) => item.inline) ? (
                        <Stack
                          direction='row'
                          spacing={2}
                          alignItems='center'
                          justifyContent='space-between'
                          sx={{ mt: 2 }}
                        >
                          {displayedFormFields.map((parameterField, index) => {
                            return (
                              <>
                                {index === 0 && <Typography variant='input-label'>{parameterField.label}</Typography>}
                                {getFieldTypes(
                                  parameterField.type,
                                  {
                                    errors,
                                    setFieldValue,
                                    values,
                                    options: getDropdownOptions(parameterField.optionsSource),
                                    fieldName: parameterField.id,
                                    placeholder: parameterField.placeholder,
                                    removeTrim: parameterField.removeTrim,
                                    setAttributeDropdownFilterDependant,
                                    loading: getDropdownLoading(parameterField.optionsSource),
                                  },
                                  index,
                                  displayedFormFields
                                )}
                              </>
                            );
                          })}
                        </Stack>
                      ) : (
                        <>
                          {displayedFormFields.map((parameterField, index) => {
                            return (
                              <Stack
                                direction='row'
                                spacing={2}
                                alignItems='center'
                                justifyContent='space-between'
                                sx={{ mt: 2 }}
                              >
                                {parameterField.label && (
                                  <Typography variant='input-label'>{parameterField.label}</Typography>
                                )}
                                {getFieldTypes(
                                  parameterField.type,
                                  {
                                    errors,
                                    setFieldValue,
                                    values,
                                    options: getDropdownOptions(parameterField.optionsSource),
                                    fieldName: parameterField.id,
                                    removeTrim: parameterField.removeTrim,
                                    setAttributeDropdownFilterDependant,
                                    loading: getDropdownLoading(parameterField.optionsSource),
                                  },
                                  index,
                                  displayedFormFields
                                )}
                              </Stack>
                            );
                          })}
                        </>
                      )}
                      {isConcatenateAttributesRule && (
                        <ConcatenationSavedParameter value={String(values.ATTRIBUTE_CONCATENATION || '')} />
                      )}
                    </Stack>

                    <Stack>
                      {unresolvedDropdownFields.length > 0 && (
                        <Alert severity='warning'>
                          Please reselect {unresolvedDropdownFields.map((field) => field.label).join(', ')} before
                          saving so their identifiers can be restored.
                        </Alert>
                      )}
                      {isErrorUpsert && <Alert severity='error'>{getErrorMessage(erroMessageUpsert)}</Alert>}
                      {isKTreeInferenceOptionsError && (
                        <Alert severity='error'>{getErrorMessage(kTreeInferenceOptionsError)}</Alert>
                      )}
                      <Divider sx={{ mb: 1, mt: 1 }} />
                      <Stack direction='row' justifyContent='flex-end' spacing={2} alignItems='center' sx={{ py: 1 }}>
                        <Button disabled={isSubmitting} variant='main-table-panel-border' onClick={handleOnCancel}>
                          Cancel
                        </Button>
                        <ButtonLoading
                          loading={isSubmitting}
                          disabled={isButtonSubmitDisabled(values) || !validationSchema?.isValidSync?.(values)}
                          variant='main-table-panel'
                          onClick={() => handleSubmit()}
                        >
                          Save Parameter
                        </ButtonLoading>
                      </Stack>
                    </Stack>
                  </>
                );
              }}
            </Formik>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
